import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import useTitanApp from "../../hooks/useTitanApp";
import { getDrugBySearch } from "../../dispatchers/nmsDispatchers";
import { searchTypes } from "../../enum/searchTypes";
import { charLimitMessage } from "../../lib/utils";

const SearchableDropdown = ({
  handleChange,
  searchType,
  selectedVal,
  options,
  maxLength,
}) => {
  const { appState } = useTitanApp();
  const { darkMode } = appState || {};

  const [inputValue, setInputValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const currentOptions = options || [];

  useEffect(() => {
    if (Array.isArray(currentOptions) && currentOptions.length > 0) {
      setSearchOptions(currentOptions);
    }
  }, [currentOptions]);

  const setDateformat = (inputDate) => {
    const date = new Date(inputDate);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const getOptionLabel = (option) => {
    if (!option) return "";
    if (searchTypes[searchType] === 0) {
      return option?.drugName?.trim() || "";
    }
    if (searchTypes[searchType] === 1)
      return `${option?.title} ${option?.forename} ${
        option?.surname
      } ${setDateformat(option?.dob)}`;
    return "";
  };

  // Sync from external selected value (string) into local state/input
  useEffect(() => {
    if (selectedVal) {
      // Try to find the matching option from current list
      const matchOpt = searchOptions.find(
        (opt) => getOptionLabel(opt).trim() === String(selectedVal).trim(),
      );
      const nextVal =
        matchOpt ||
        (searchTypes[searchType] === 0 ? { drugName: selectedVal } : null);
      setSelected(nextVal || null);
      setInputValue(String(selectedVal));
    } else {
      setSelected(null);
      setInputValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVal]);

  const handleSearch = async (rawQuery) => {
    // Never send more than maxLength characters to Drug/search
    const query = maxLength ? (rawQuery || "").slice(0, maxLength) : rawQuery;
    setInputValue(query);
    if (!query || query.length < 2) {
      setSearchOptions(currentOptions);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        keyword: query.toLowerCase(),
      };
      const response = await getDrugBySearch(payload);

      if (Array.isArray(response)) {
        setSearchOptions(response);
      } else {
        console.warn("Unexpected response format", response);
        setSearchOptions([]);
      }
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const isAtLimit = !!maxLength && inputValue.length >= maxLength;

  return (
    <div style={{ width: "100%" }}>
      <Autocomplete
        fullWidth
        disableClearable
        disablePortal
        loading={loading}
        options={searchOptions}
        value={selected}
        inputValue={inputValue}
        onInputChange={(e, val) => {
          if (e && e.type === "change") {
            handleSearch(val);
          } else {
            setInputValue(val || "");
          }
        }}
        onChange={(e, val) => {
          if (val) {
            setSelected(val);
            setInputValue(getOptionLabel(val));
            handleChange(val);
          } else {
            setSelected(null);
            setInputValue("");
            handleChange(null);
          }
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          const optionLabel = getOptionLabel(option).trim();
          const valueLabel = getOptionLabel(value).trim();
          return optionLabel === valueLabel;
        }}
        filterOptions={(opts, { inputValue }) => {
          const terms = inputValue.toLowerCase().split(" ").filter(Boolean);
          return opts.filter((option) => {
            const label = getOptionLabel(option).toLowerCase();
            return terms.every((term) => label.includes(term));
          });
        }}
        renderOption={(props, option) => {
          const label = getOptionLabel(option);
          const matches = match(label, inputValue, { insideWords: true });
          const parts = parse(label, matches);

          return (
            <li
              {...props}
              onMouseDown={(ev) => ev.preventDefault()} // keep focus to avoid blur-before-select
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                display: "block",
                lineHeight: 1.4,
              }}
            >
              {parts?.map((part, index) => (
                <span
                  key={index}
                  style={{
                    color: part.highlight
                      ? "#1a73e8"
                      : darkMode
                        ? "#f2f2f2"
                        : "#222",
                    fontWeight: part.highlight ? 600 : 400,
                    fontSize: "0.9rem",
                    display: "inline",
                  }}
                >
                  {part.text}
                </span>
              ))}
            </li>
          );
        }}
        ListboxProps={{ style: { pointerEvents: "auto" } }}
        sx={{
          "& .MuiAutocomplete-option": {
            backgroundColor: darkMode ? "#000" : "#f2f2f2",
            color: darkMode ? "#fff" : "#000",
            "&:hover": {
              backgroundColor: darkMode ? "#222" : "#ddd",
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="e.g., Amlodipine 10mg tablets"
            variant="outlined"
            autoComplete="off"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                border: `1px solid ${darkMode ? "#3c3c3c" : "#eeeeee"}`,
                padding: "0px",
                "& .MuiInputBase-input": {
                  paddingTop: "0.5rem",
                  paddingBottom: "0.5rem",
                  paddingLeft: "0.75rem",
                  paddingRight: "0.75rem",
                  fontSize: "14px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: darkMode ? "#000" : "#eeeeee",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: darkMode ? "#888" : "#000",
                fontSize: "0.9rem",
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{loading && <CircularProgress size={20} />}</>,
            }}
            inputProps={{
              ...params.inputProps,
              ...(maxLength ? { maxLength } : {}),
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: darkMode ? "#222" : "#fff",
              color: darkMode ? "#fff" : "#222",
              border: `1px solid ${darkMode ? "#3c3c3c" : "#eeeeee"}`,
              zIndex: 4000,

              "& .MuiAutocomplete-noOptions": {
                color: darkMode ? "#fff" : "#222",
                backgroundColor: darkMode ? "#222" : "#fff",
              },
              "& .MuiAutocomplete-loading": {
                color: darkMode ? "#fff" : "#222",
                backgroundColor: darkMode ? "#222" : "#fff",
              },
            },
          },
          popper: {
            sx: { zIndex: 4000 },
          },
        }}
      />
      {isAtLimit && (
        <p
          className={`text-sm mt-1 ${
            darkMode ? "text-red-400" : "text-red-600"
          }`}
        >
          {charLimitMessage(maxLength)}
        </p>
      )}
    </div>
  );
};

export default SearchableDropdown;
